import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Percent, AlertCircle, Save } from "lucide-react";

const FIELDS = [
  { key: "tax_federal_pct", label: "Federal Income Tax %", help: "Applied to gross pay for W2 employees with tax withholding enabled." },
  { key: "tax_state_pct", label: "State Income Tax %", help: "Applied to gross pay." },
  { key: "tax_local_pct", label: "Local / City Tax %", help: "Applied to gross pay." },
  { key: "tax_ss_pct", label: "Social Security %", help: "U.S. standard is 6.20% (employee portion)." },
  { key: "tax_medicare_pct", label: "Medicare %", help: "U.S. standard is 1.45% (employee portion). Higher earners may pay an additional 0.9%." },
];

export default function AdminTaxes() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const branding = useBranding();

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        taxes_enabled_globally: !!form.taxes_enabled_globally,
        tax_federal_pct: Number(form.tax_federal_pct) || 0,
        tax_state_pct: Number(form.tax_state_pct) || 0,
        tax_local_pct: Number(form.tax_local_pct) || 0,
        tax_ss_pct: Number(form.tax_ss_pct) || 0,
        tax_medicare_pct: Number(form.tax_medicare_pct) || 0,
      };
      const { data } = await api.put("/settings", payload);
      setForm(data);
      await branding.refresh();
      toast.success("Tax settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!form) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  const totalPct =
    (Number(form.tax_federal_pct) || 0) +
    (Number(form.tax_state_pct) || 0) +
    (Number(form.tax_local_pct) || 0) +
    (Number(form.tax_ss_pct) || 0) +
    (Number(form.tax_medicare_pct) || 0);

  const enabled = !!form.taxes_enabled_globally;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="admin-taxes-page">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</p>
        <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Tax Withholdings</h1>
        <p className="text-sm text-slate-500 mt-1">
          When the global toggle is on, paystubs auto-fill these percentages against the gross pay for employees with tax withholding enabled.
          Admins can still override individual paystub deductions.
        </p>
      </header>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-bold font-display">Global Tax Withholdings</h3>
            <p className="text-sm text-slate-500">{enabled ? "Enabled — paystubs will auto-apply the percentages below." : "Disabled — paystubs are generated with $0 deductions."}</p>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={enabled}
              onCheckedChange={(v) => setForm({ ...form, taxes_enabled_globally: v })}
              data-testid="taxes-enabled-switch"
            />
            <span className="text-sm font-semibold">{enabled ? "On" : "Off"}</span>
          </div>
        </CardContent>
      </Card>

      {!enabled && (
        <Card className="rounded-2xl border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3 text-sm text-amber-900">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p>Tax withholdings are currently <strong>off</strong>. Turn the toggle on if your company processes W2 payroll with tax withholdings. 1099 contractors typically have this disabled.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <Card key={f.key} className="rounded-2xl border-slate-200">
            <CardContent className="p-4 space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">{f.label}</Label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form[f.key] ?? 0}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  disabled={!enabled}
                  className="pr-9"
                  data-testid={`tax-input-${f.key}`}
                />
                <Percent className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
              <p className="text-xs text-slate-500 leading-snug">{f.help}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="rounded-2xl bg-slate-900 text-white">
        <CardContent className="p-5 flex items-baseline justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400">Total withholding</p>
            <p className="text-sm text-slate-300">Applied to gross pay on each paystub</p>
          </div>
          <p className="text-4xl font-extrabold font-display text-green-400">{totalPct.toFixed(2)}%</p>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={busy} className="bg-sky-600 hover:bg-sky-700 h-12 px-6" data-testid="save-taxes-btn">
        <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save tax settings"}
      </Button>
    </div>
  );
}
